import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle,
  SkipForward, Loader2, Sparkles, RefreshCw,
} from "lucide-react";
import StepLayout from "./StepLayout";

const TYPES = [
  { key: "hygiene",     label: "Daily Hygiene",     desc: "Per-facility hygiene spot-check (PASS/FAIL items)." },
  { key: "cleanliness", label: "Daily Cleanliness", desc: "Per-facility cleanliness checklist by sub-area." },
  { key: "ccp",         label: "CCP Readings",      desc: "Temperature / parameter readings per equipment." },
];

/**
 * Best-effort error message extractor. Handles:
 *   - Backend JSON 4xx/5xx with `{ detail }` (string OR Pydantic list)
 *   - Cloudflare/edge HTML 502/520 pages (returns a friendly "server is
 *     under load" message instead of the raw HTML)
 *   - Network errors with no response
 *   - axios `responseType: "blob"` paths (handled in the async sibling)
 */
function parseHttpError(e, fallback) {
  if (!e?.response) {
    if (e?.code === "ECONNABORTED") return "Request timed out — please retry.";
    return e?.message ? `${e.message}` : (fallback || "Something went wrong.");
  }
  const status = e.response.status;
  const data = e.response.data;
  if (data && typeof data === "object" && data.detail !== undefined) {
    return formatErr(data.detail);
  }
  if (typeof data === "string" && data.length > 0) {
    // Filter out HTML — most edge errors arrive as a full HTML page.
    if (/<html|cloudflare|<!doctype/i.test(data)) {
      if (status === 502 || status === 504 || status === 520 || status === 521 || status === 522 || status === 524) {
        return `The server didn't respond in time (HTTP ${status}). This usually means the file is too large or the server is busy — please retry in a moment.`;
      }
      return `Server returned an HTML error page (HTTP ${status}). Please retry; if it persists, contact support.`;
    }
    return data.slice(0, 240);
  }
  return fallback || `Request failed (HTTP ${status}).`;
}

/** Async variant for axios `responseType: "blob"` requests where the error
 * body is a Blob that needs to be unwrapped via `.text()`. */
async function parseHttpErrorAsync(e, fallback) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const j = JSON.parse(text);
        return formatErr(j.detail) || fallback;
      } catch { /* not JSON */ }
      // It's HTML or plain text — run through the sync parser by stashing
      // the unwrapped string back on the response.
      e.response.data = text;
    } catch { /* read failure */ }
  }
  return parseHttpError(e, fallback);
}

/**
 * Step 7 — Catch-up Mode.
 *
 * Owner downloads one Excel per (site × type), fills it offline, uploads
 * back. The upload runs in two phases — preview shows errors; commit
 * writes the rows. The wizard's catchup flag only flips when at least
 * one commit succeeds OR the user explicitly chooses "Skip — no
 * historical data".
 */
export default function Step7Catchup({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify, confirm } = useDialog();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // active sub-flow card per (site,type)
  // upload state map keyed by `${site_id}|${type}`
  const [uploads, setUploads] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/catchup/inventory");
      setInv(data);
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const downloadTpl = async (siteId, type) => {
    setErr("");
    try {
      const res = await api.get("/onboarding/catchup/template", {
        params: { site_id: siteId, type },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const siteName = (inv?.sites || []).find((s) => s.site_id === siteId)?.site_name?.replace(/[^A-Za-z0-9]/g, "_") || "site";
      a.href = url;
      a.download = `VB_Catchup_${type}_${siteName}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(await parseHttpErrorAsync(e, "Could not download template — please try again."));
    }
  };

  const upload = async (siteId, type, file, commit = false) => {
    const key = `${siteId}|${type}`;
    setUploads((u) => ({ ...u, [key]: { ...(u[key] || {}), busy: true, mode: commit ? "commit" : "preview" } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/onboarding/catchup/upload?site_id=${siteId}&type=${type}&commit=${commit}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploads((u) => ({ ...u, [key]: { busy: false, result: data, filename: file.name, mode: commit ? "commit" : "preview" } }));
      if (commit && data.committed > 0) {
        await notify({
          title: "Records imported",
          message: `${data.committed} historical entries saved to your live system.`,
          variant: "ok",
        });
      }
    } catch (e) {
      setUploads((u) => ({ ...u, [key]: { busy: false, error: parseHttpError(e, "Upload failed — please try again.") } }));
    }
  };

  const skip = async () => {
    const ok = await confirm({
      title: "Skip catch-up?",
      message: "You can always upload historical data later from the CCP Data Submission module. Continue to finish setup?",
      confirmLabel: "Yes, finish setup",
      cancelLabel: "Keep uploading",
      variant: "warn",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/onboarding/catchup/skip");
      await onAdvance({ catchup: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const finishCatchup = async () => {
    // If user has committed at least one record OR skipped, the catchup
    // flag is already true. Otherwise we still set it via onAdvance.
    await onAdvance({ catchup: true });
  };

  // Did the owner commit anything for any (site,type)?
  const anyCommitted = Object.values(uploads).some(
    (u) => u?.result?.committed > 0,
  );

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Catch up your records"
      subtitle="Bring your old paper logs into Vanguard Beacon — one Excel per site, per type."
      description={
        <div className="space-y-3">
          <p>For each site, pick what you want to import (Hygiene, Cleanliness, or CCP readings) and download the template.</p>
          <p>The template comes pre-filled with the right facilities, items, and dropdowns. Fill it offline, then upload — we'll preview any issues before saving.</p>
          <p className="text-xs opacity-70">Got nothing to import? Hit <em>Skip</em> below — you can always upload later from <strong>CCP Data Submission</strong>.</p>
          {!inv?.start_date && (
            <div className="vb-pill warn text-xs mt-2">
              <AlertCircle size={11} /> Set a <strong>data collection start date</strong> in Step 2a first — historical uploads are validated against it.
            </div>
          )}
        </div>
      }
      illustration={
        <div className="vb-card p-4" style={{ background: "var(--vb-bg)" }}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} style={{ color: "var(--vb-accent)" }} />
            <div className="vb-heading text-sm m-0" style={{ color: "var(--vb-text-2)" }}>Smart Excel templates</div>
          </div>
          <p className="text-[11px] opacity-70 m-0">
            Pre-loaded dropdowns and date guards keep your team from typing wrong facility names or out-of-range values.
          </p>
        </div>
      }
      onBack={onBack}
      onNext={finishCatchup}
      onFinishLater={onFinishLater}
      isFinal
      nextLabel={anyCommitted ? "Finish setup" : "Mark catch-up done"}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm opacity-70">
            Period:&nbsp;
            <strong>{inv?.start_date || "—"}</strong> → <strong>{inv?.today || "today"}</strong>
          </div>
          <div className="flex gap-2">
            <button type="button" className="vb-btn ghost sm"
              onClick={load} disabled={busy} data-testid="catchup-reload-btn">
              <RefreshCw size={12} /> Refresh inventory
            </button>
            <button type="button" className="vb-btn ghost sm"
              onClick={skip} disabled={busy} data-testid="catchup-skip-btn">
              <SkipForward size={12} /> Skip — no historical data
            </button>
          </div>
        </div>

        {!inv ? (
          <div className="vb-card p-6 text-center opacity-70" data-testid="catchup-loading">
            <Loader2 size={20} className="inline animate-spin" /> Loading inventory…
          </div>
        ) : inv.sites.length === 0 ? (
          <div className="vb-card p-6 text-sm opacity-70" data-testid="catchup-no-sites">
            No sites yet — go back to Step 2b first.
          </div>
        ) : (
          inv.sites.map((s) => (
            <SiteCatchupCard key={s.site_id} site={s}
              uploads={uploads}
              onDownload={(t) => downloadTpl(s.site_id, t)}
              onUpload={(t, f) => upload(s.site_id, t, f, false)}
              onCommit={(t, f) => upload(s.site_id, t, f, true)} />
          ))
        )}
      </div>
    </StepLayout>
  );
}

function SiteCatchupCard({ site, uploads, onDownload, onUpload, onCommit }) {
  return (
    <div className="vb-card p-5" data-testid={`catchup-site-${site.site_id}`}>
      <div className="vb-heading text-lg mb-3" style={{ color: "var(--vb-text-2)" }}>
        {site.site_name}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TYPES.map((t) => {
          const eligibleCount =
            t.key === "hygiene"     ? site.hygiene_facilities :
            t.key === "cleanliness" ? site.cleanliness_facilities :
                                      site.ccp_equipment;
          const key = `${site.site_id}|${t.key}`;
          const state = uploads[key] || {};
          return (
            <TypeCard
              key={t.key}
              type={t} eligibleCount={eligibleCount}
              state={state}
              onDownload={() => onDownload(t.key)}
              onUpload={(f) => onUpload(t.key, f)}
              onCommit={(f) => onCommit(t.key, f)}
              testKey={`${site.site_id}-${t.key}`} />
          );
        })}
      </div>
    </div>
  );
}

function TypeCard({ type, eligibleCount, state, onDownload, onUpload, onCommit, testKey }) {
  const disabled = eligibleCount === 0;
  const previewOk = state?.result?.ok && state.mode === "preview";
  const committed = state?.result?.committed > 0;
  const showErrors = state?.result && !state.result.ok;
  // Re-use the same file across preview & commit
  const [stagedFile, setStagedFile] = useState(null);
  const fileInputId = `catchup-file-${testKey}`;

  return (
    <div className="vb-card p-3" data-testid={`catchup-type-${testKey}`}
      style={{ background: "var(--vb-bg-2)", border: "1px solid var(--vb-border)" }}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="vb-heading text-sm m-0" style={{ color: "var(--vb-text-2)" }}>
            <FileSpreadsheet size={13} className="inline -mt-0.5 mr-1 opacity-70" />
            {type.label}
          </div>
          <p className="text-[11px] opacity-60 m-0">{eligibleCount} eligible {type.key === "ccp" ? "equipment" : "facilities"}</p>
        </div>
        {committed && <CheckCircle2 size={16} style={{ color: "var(--vb-accent)" }} data-testid={`catchup-${testKey}-committed`} />}
      </div>
      <p className="text-[11px] opacity-70 mb-2">{type.desc}</p>

      <div className="flex flex-col gap-1.5">
        <button type="button" className="vb-btn ghost sm w-full"
          onClick={onDownload} disabled={disabled}
          data-testid={`catchup-${testKey}-download`}>
          <Download size={11} /> Download template
        </button>

        <input id={fileInputId} type="file" accept=".xlsx" className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]; if (!f) return;
            setStagedFile(f);
            onUpload(f);
            e.target.value = "";
          }}
          data-testid={`catchup-${testKey}-file-input`} />
        <button type="button" className="vb-btn ghost sm w-full"
          disabled={disabled || state?.busy}
          onClick={() => document.getElementById(fileInputId)?.click()}
          data-testid={`catchup-${testKey}-upload`}>
          {state?.busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          {state?.busy
            ? (state.mode === "commit" ? "Committing…" : "Validating…")
            : (committed ? "Re-upload" : "Upload completed file")}
        </button>

        {previewOk && stagedFile && !committed && (
          <button type="button" className="vb-btn sm w-full"
            disabled={state?.busy}
            onClick={() => onCommit(stagedFile)}
            data-testid={`catchup-${testKey}-commit`}>
            <CheckCircle2 size={11} /> Commit {state.result.rows_valid} record{state.result.rows_valid === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {/* Status / errors */}
      {state?.error && (
        <div className="mt-2 rounded-lg p-3 text-[11px] leading-snug"
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            wordBreak: "break-word",
          }}
          data-testid={`catchup-${testKey}-err`}>
          <div className="font-semibold flex items-center gap-1.5 mb-0.5">
            <AlertCircle size={12} /> Upload failed
          </div>
          {state.error}
        </div>
      )}
      {committed && (
        <div className="vb-pill ok mt-2 text-[10px] w-full" data-testid={`catchup-${testKey}-ok`}>
          ✓ {state.result.committed} record{state.result.committed === 1 ? "" : "s"} imported
        </div>
      )}
      {previewOk && !committed && (
        <div className="vb-pill mt-2 text-[10px] w-full" style={{ background: "var(--vb-bg)", color: "var(--vb-text-2)" }}>
          ✓ {state.result.rows_valid} rows ready — click Commit to save
        </div>
      )}
      {showErrors && (
        <div className="mt-2" data-testid={`catchup-${testKey}-errs`}>
          {state.result.rows_total === 0 ? (
            <div className="vb-pill warn text-[10px] w-full">
              No data rows found — fill at least one row before uploading.
            </div>
          ) : (
            <>
              <div className="vb-pill err text-[10px] w-full mb-1">
                ✗ {state.result.errors.length} issue{state.result.errors.length === 1 ? "" : "s"}, {state.result.rows_valid}/{state.result.rows_total} rows ok
              </div>
              <div className="text-[10px] opacity-80 max-h-32 overflow-y-auto space-y-0.5 pl-2">
                {state.result.errors.slice(0, 6).map((err, i) => (
                  <div key={i}>
                    <strong>{err.sheet}</strong>{err.col ? ` · ${err.col}` : ""}: {err.message}
                  </div>
                ))}
                {state.result.errors.length > 6 && (
                  <div className="opacity-60">+ {state.result.errors.length - 6} more — fix and re-upload</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
